import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createContext } from 'react';
import Cookies from 'js-cookie';
import { ISignIn } from '../../types';

interface IAuthContext {
  handleSignInData: (params: ISignIn) => void;
  handleSignOut: () => void;
  logged: boolean;
  setLogged: React.Dispatch<React.SetStateAction<boolean>>;
  userEmail: string;
  setUserEmail: React.Dispatch<React.SetStateAction<string>>;
  userId: string;
  setUserId: React.Dispatch<React.SetStateAction<string>>;
}

const AuthContext = createContext({} as IAuthContext);

export const useAuthContext = () => {
  return useContext(AuthContext);
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [logged, setLogged] = useState(() => {
    const isLogged = Cookies.get('@logged');
    return !!isLogged;
  });
  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const validateSession = () => {
    const token = Cookies.get('@token');
    const expiration = Cookies.get('@token_expiration');

    if (!token || !expiration) {
      handleSignOut();
      return;
    }

    const currentTime = Date.now();
    const expirationTime = parseInt(expiration, 10);

    if (currentTime >= expirationTime) {
      handleSignOut();
    } else {
      const timeout = expirationTime - currentTime;
      setLogged(true);
      setTimeout(() => handleSignOut(), timeout);
    }
  };

  const handleSignInData = useCallback((params: ISignIn) => {
    const { token, userEmail, userId, expiresIn } = params;

    const expirationTime = Date.now() + expiresIn * 1000; // Converte segundos para milissegundos
    Cookies.set('@token', token);
    Cookies.set('@token_expiration', expirationTime.toString());
    Cookies.set('@userEmail', userEmail);
    Cookies.set('@logged', 'true');
    Cookies.set('@user_id', userId);

    setLogged(true);
    setUserEmail(userEmail);
    setUserId(userId);
  }, []);

  const handleSignOut = useCallback(() => {
    Cookies.remove('@logged');
    Cookies.remove('@token');
    Cookies.remove('@user_id');
    Cookies.remove('@userEmail');
    Cookies.remove('@token_expiration');
    setLogged(false);
  }, []);

  const authContextWrapper = useMemo(
    () => ({
      logged,
      setLogged,
      userId,
      setUserId,
      userEmail,
      setUserEmail,
      handleSignInData,
      handleSignOut,
    }),
    [
      logged,
      setLogged,
      userId,
      setUserId,
      userEmail,
      setUserEmail,
      handleSignInData,
      handleSignOut,
    ],
  );

  useEffect(() => {
    validateSession();
  }, []);

  return (
    <AuthContext.Provider value={authContextWrapper}>
      {children}
    </AuthContext.Provider>
  );
};
